import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  CalendarDays,
  Calendar,
  User,
  Tag,
  Clock,
  ArrowLeft,
  Bookmark,
  Share2,
  ThumbsUp,
  MessageSquare,
} from "lucide-react";

const BlogView = () => {
  const [blog, setBlog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formattedContent, setFormattedContent] = useState("");
  const { id } = useParams();
  const [relatedBlogs, setRelatedBlogs] = useState([]);

  useEffect(() => {
    const fetchRelatedBlogs = async () => {
      try {
        const response = await fetch("https://apigrand.dodunsoftsolutions.com/api/blogs");
        const data = await response.json();

        // Filter blogs with common tags
        const filtered = data
          .filter(
            (b) =>
              b._id !== blog._id &&
              b.tags?.some((tag) => blog.tags.includes(tag))
          )
          .slice(0, 3);

        setRelatedBlogs(filtered);
      } catch (err) {
        console.error("Error fetching related blogs:", err);
      }
    };

    if (blog && blog.tags?.length > 0) {
      fetchRelatedBlogs();
    }
  }, [blog]);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`https://apigrand.dodunsoftsolutions.com/api/blogs/${id}`);

        if (!response.ok) {
          throw new Error("Failed to fetch blog post");
        }

        const data = await response.json();
        setBlog(data);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching blog:", err);
      } finally {
        // Add a slight delay to show the loading animation
        setTimeout(() => setIsLoading(false), 600);
      }
    };

    fetchBlog();
  }, [id]);

  useEffect(() => {
    if (blog && blog.content) {
      // Process the content to add line breaks after periods
      const processed = formatContentWithLineBreaks(blog.content);
      setFormattedContent(processed);
    }
  }, [id, blog]);

  // Function to format content with line breaks
  const formatContentWithLineBreaks = (content) => {
    // Split the content by periods followed by a space
    const sentences = content.split(/\.\s/);

    let result = "";
    let lineCount = 0;

    // Process each sentence
    sentences.forEach((sentence, index) => {
      if (index < sentences.length - 1) {
        // Add the sentence back with its period
        result += sentence + ". ";

        lineCount++;

        // After every 4-5 lines, add a line break
        if (lineCount >= 4 && lineCount <= 5) {
          result += "</br> ";
          lineCount = 0;
        }
      } else {
        // Handle the last sentence (which might not end with a period)
        result += sentence;
      }
    });

    return result;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const getReadTime = (content) => {
    // Assuming an average reading speed of 200 words per minute
    const wordCount = content ? content.split(/\s+/).length : 0;
    const readTime = Math.ceil(wordCount / 200);
    return readTime > 0 ? readTime : 1; // Minimum 1 minute read time
  };

  // Calculate reading time based on content length
  const calculateReadingTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / wordsPerMinute);
    return readingTime < 1 ? 1 : readingTime;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50 p-4">
        <div className="animate-pulse flex flex-col items-center space-y-8 w-full max-w-4xl">
          <div className="h-6 w-32 bg-gray-300 rounded-md"></div>
          <div className="h-16 w-3/4 bg-gray-300 rounded-md"></div>
          <div className="h-72 w-full bg-gray-300 rounded-lg"></div>
          <div className="w-full space-y-4">
            <div className="h-4 w-full bg-gray-300 rounded"></div>
            <div className="h-4 w-5/6 bg-gray-300 rounded"></div>
            <div className="h-4 w-4/6 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="text-red-500 text-6xl mb-6">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Unable to load blog
          </h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={() => window.history.back()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md transition-colors flex items-center mx-auto transform hover:scale-105 duration-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="text-gray-400 text-6xl mb-6">🔍</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Blog not found
          </h2>
          <p className="text-gray-600 mb-8">
            The blog post you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => window.history.back()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md transition-colors flex items-center mx-auto transform hover:scale-105 duration-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const readingTime = calculateReadingTime(blog.content);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero section with parallax effect */}
      <div className="relative overflow-hidden">
        <div
          className="w-full h-96 md:h-screen md:max-h-[600px] bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: `url(${
              blog.coverImage ||
              "https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&q=80"
            })`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/80"></div>
        </div>

        <div className="container mx-auto px-4 absolute inset-0 flex flex-col justify-end pb-12">
          <div className="relative z-10 max-w-4xl mx-auto">
            {/* Status badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              {blog.status === "draft" && (
                <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium inline-flex items-center">
                  Draft
                </span>
              )}
              {blog.status === "archived" && (
                <span className="bg-gray-500 text-white px-3 py-1 rounded-full text-sm font-medium inline-flex items-center">
                  Archived
                </span>
              )}
              {blog.isFeatured && blog.status === "published" && (
                <span className="bg-gradient-to-r from-purple-600 to-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium inline-flex items-center">
                  Featured
                </span>
              )}
            </div>

            {/* Title with animation */}
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {blog.title}
            </h1>

            {/* Author and metadata */}
            <div className="flex flex-wrap items-center text-white gap-4 text-sm md:text-base">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                <span>{blog.author}</span>
              </div>
              <div className="flex items-center">
                <CalendarDays className="w-4 h-4 mr-2" />
                <span>{formatDate(blog.publishedAt)}</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                <span>{readingTime} min read</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-auto mx-auto bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all duration-500 animate-fadeIn">
          {/* Blog content */}
          <div className="p-6 md:p-12">
            {/* Excerpt with styled quote */}
            {blog.excerpt && (
              <div className="text-xl text-gray-600 mb-10 font-serif italic border-l-4 border-blue-500 pl-6 py-2 bg-blue-50 rounded-r-lg">
                {blog.excerpt}
              </div>
            )}

            {/* Main content with improved typography */}
            <div className="text-justify prose prose-lg md:prose-xl max-w-none prose-headings:text-blue-900 prose-a:text-blue-600 prose-a:no-underline prose-a:font-medium hover:prose-a:text-blue-800 hover:prose-a:underline prose-img:rounded-xl prose-img:shadow-md">
              {/* Render content as HTML - Note: In a production app, use a proper HTML sanitizer */}
              <div dangerouslySetInnerHTML={{ __html: formattedContent }} />
            </div>

            {/* Tags with improved styling */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center">
                  <Tag className="w-4 h-4 mr-2" />
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {blog.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-gray-100 hover:bg-blue-50 text-gray-800 hover:text-blue-700 px-4 py-2 rounded-full text-sm cursor-pointer transition-colors border border-transparent hover:border-blue-200"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Author profile */}
            <div className="mt-12 pt-6 border-t border-gray-200">
              <div className="flex items-start gap-5">
                <div className="w-16 h-16 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                  {/* Placeholder for author avatar */}
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <User className="w-8 h-8" />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900">
                    {blog.author}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {relatedBlogs.length > 0 && (
        <div className="container mx-auto px-4 pb-20">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">
            Related Blogs
          </h2>
          <div className="grid gap-6 md:grid-cols-3 2xl:grid-cols-4">
            {relatedBlogs.map((related) => (
              <div
                className={`bg-white border-gray-100 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition duration-300 h-full flex flex-col border`}
              >
                <div className="relative aspect-video overflow-hidden bg-gray-100">
                  <img
                    src={related.coverImage || "/api/placeholder/600/400"}
                    alt={related.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition duration-500"
                  />
                  <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  {related.status !== "published" && (
                    <div className="absolute top-3 left-3 bg-gray-900/80 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1 rounded-full">
                      {related.status}
                    </div>
                  )}
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex-1">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {related.tags &&
                        related.tags.map((tag, index) =>
                          index < 3 ? (
                            <span
                              key={index}
                              className={`bg-teal-50 text-teal-800 text-xs font-medium px-2.5 py-0.5 rounded-full`}
                            >
                              {tag}
                            </span>
                          ) : null
                        )}
                      {related.tags && related.tags.length > 3 && (
                        <span
                          className={`bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full`}
                        >
                          +{related.tags.length - 3}
                        </span>
                      )}
                    </div>
                    <h3
                      className={`text-xl font-bold mb-3 text-gray-900 group-hover:text-teal-600 transition duration-200 line-clamp-2`}
                    >
                      {related.title}
                    </h3>
                    {related.excerpt && (
                      <p className={`text-gray-600 text-sm mb-4 line-clamp-3`}>
                        {related.excerpt}
                      </p>
                    )}
                  </div>
                  <div
                    className={`flex items-center justify-between pt-4 border-t border-gray-100`}
                  >
                    <div className="flex items-center space-x-1">
                      <User className="h-4 w-4 text-teal-500" />
                      <span className={`text-sm text-gray-600`}>
                        {related.author}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4 text-teal-500" />
                        <span className={`text-sm text-gray-600`}>
                          {getReadTime(related.content || related.excerpt)} min
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4 text-teal-500" />
                        <span className={`text-sm text-gray-600`}>
                          {formatDate(related.publishedAt).split(",")[0]}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add some custom CSS for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default BlogView;
